import { useCallback, useEffect, useRef, useState } from 'react';
import { AudioLines, Play, Square } from 'lucide-react';
import { Panel } from './Panel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const url = (p) => `${API}/professor/audio/${p}`;
const PHRASE = [['stroke', 1.6], ['tap', 0], ['massage', 0], ['stroke', 1.6]];

// one AudioContext + decoded-buffer cache for the whole panel
let ctx = null;
const buffers = new Map();
const audio = () => ctx || (ctx = new (window.AudioContext || window.webkitAudioContext)());
async function buffer(path) {
  if (!buffers.has(path)) buffers.set(path, fetch(url(path)).then((r) => r.arrayBuffer()).then((b) => audio().decodeAudioData(b)));
  return buffers.get(path);
}
function playBuf(buf, { loop = false, seconds = 0, at = 0 } = {}) {
  const c = audio();
  const src = c.createBufferSource();
  const g = c.createGain();
  src.buffer = buf;
  src.loop = loop;
  src.connect(g).connect(c.destination);
  const t0 = c.currentTime + at;
  src.start(t0);
  const len = loop ? seconds : buf.duration;
  if (loop) {
    g.gain.setValueAtTime(1, t0 + len - 0.25);
    g.gain.linearRampToValueAtTime(0, t0 + len);
    src.stop(t0 + len + 0.05);
  }
  return { src, len };
}

const SoundButton = ({ path, loop, label, testId, playing, setPlaying }) => {
  const on = playing.key === testId;
  const click = async () => {
    if (playing.src) playing.src.stop();
    if (on) return setPlaying({});
    const { src } = playBuf(await buffer(path), { loop, seconds: loop ? 3.5 : 0 });
    src.onended = () => setPlaying((p) => (p.key === testId ? {} : p));
    setPlaying({ key: testId, src });
  };
  return (
    <button type="button" data-testid={testId} onClick={click} className={`btn-lab inline-flex items-center gap-2 ${on ? 'border-[var(--spore)] text-[var(--spore)]' : ''}`}>
      {on ? <Square size={12} /> : <Play size={12} />} {label}
    </button>
  );
};

const TypeRow = ({ t, cands, choice, onChoose, playing, setPlaying }) => (
  <div data-testid={`sound-type-${t.type}`} className="border-b border-white/5 py-3">
    <div className="mb-2 flex items-center justify-between text-xs tracking-[0.15em]">
      <span className="text-[var(--ink)]">{t.label}</span>
      <span data-testid={`sound-choice-${t.type}`} className={choice && choice !== 'current' ? 'text-[var(--spore)]' : 'text-[var(--dim)]'}>{choice && choice !== 'current' ? `CHOSEN · ${choice.split('.').slice(1).join(' · ')}` : 'KEEPING CURRENT'}</span>
    </div>
    <div className="flex flex-wrap items-center gap-2">
      {t.current && <SoundButton path={t.current.audio_path} loop={t.loop} label="CURRENT" testId={`sound-${t.type}-current`} playing={playing} setPlaying={setPlaying} />}
      {t.current && choice && choice !== 'current' && <button type="button" data-testid={`sound-${t.type}-keep-current`} className="btn-lab text-xs" onClick={() => onChoose(t.type, 'current')}>keep current</button>}
      <span className="mx-2 h-5 w-px bg-white/10" />
      {cands.map((c) => (
        <span key={c.id} className={`inline-flex items-center gap-1 border px-1 py-1 ${choice === c.id ? 'border-[var(--spore)]' : 'border-transparent'}`}>
          <SoundButton path={c.audio_path} loop={c.loop} label={`${c.id.split('.')[1].replace('sfx_', 'SFX ').replace('tts_', 'TTS v3 ')} · ${c.variant}`} testId={`sound-${c.id}`} playing={playing} setPlaying={setPlaying} />
          <button type="button" data-testid={`use-${c.id}`} className="btn-lab text-xs" disabled={choice === c.id} onClick={() => onChoose(t.type, c.id)}>{choice === c.id ? '✓ chosen' : 'Use this'}</button>
        </span>
      ))}
    </div>
  </div>
);

export const SporeSoundsPanel = () => {
  const [d, setD] = useState(null);
  const [playing, setPlaying] = useState({});
  const [msg, setMsg] = useState('');
  const poll = useRef(null);
  const load = useCallback(() => fetch(`${API}/sfx/audition/current`).then((r) => r.json()).then(setD).catch((e) => setMsg(String(e))), []);
  useEffect(() => {
    load();
    poll.current = setInterval(() => setD((cur) => (cur && cur.running ? (load(), cur) : cur)), 3000);
    return () => clearInterval(poll.current);
  }, [load]);
  if (!d) return null;
  const a = d.audition;
  const choices = (a && a.choices) || {};
  const pathFor = (type) => {
    const t = d.types.find((x) => x.type === type);
    const c = a && choices[type] && choices[type] !== 'current' ? a.candidates.find((x) => x.id === choices[type]) : null;
    return c ? c.audio_path : t && t.current && t.current.audio_path;
  };
  const choose = async (type, candidate_id) => {
    await fetch(`${API}/sfx/audition/choose`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ type, candidate_id }) });
    load();
  };
  const phrase = async () => {
    const bufs = await Promise.all(PHRASE.map(([type]) => buffer(pathFor(type))));
    let at = 0.05;
    PHRASE.forEach(([type, sec], i) => {
      const { len } = playBuf(bufs[i], { loop: type === 'stroke', seconds: sec, at });
      at += len + 0.45;
    });
  };
  const apply = async () => {
    setMsg('APPLYING…');
    const r = await fetch(`${API}/sfx/audition/apply`, { method: 'POST' });
    const j = await r.json();
    setMsg(r.ok ? `APPLIED · NEW SFX BANK ${j.bank_id} · SEED UPDATED · RELOAD THE STAGE (and re-deploy for the live site)` : `APPLY FAILED · ${j.detail}`);
    load();
  };
  const generate = async () => {
    await fetch(`${API}/sfx/audition/generate`, { method: 'POST' });
    load();
  };
  const nChosen = Object.values(choices).filter((c) => c !== 'current').length;
  return (
    <Panel testId="spore-sounds-panel" code="11 / SPECIMEN VOICE" title={<>SPORE<span className="normal-case">s</span> sounds · audition</>} right={<AudioLines size={18} className="text-[var(--teal)]" />}>
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--muted)]">
          <span data-testid="spore-sounds-status">
            {d.running ? `GENERATING CANDIDATES ${d.running.done}/${d.running.total}…` : a ? `AUDITION ${a.audition_id} · ${a.candidates.length} CANDIDATES · ${a.credits_used} CREDITS · LIVE BANK ${d.bank_id}` : 'NO CANDIDATES YET'}
          </span>
          {!a && !d.running && <button type="button" data-testid="spore-sounds-generate" className="btn-lab" onClick={generate}>Generate candidates</button>}
          <button type="button" data-testid="spore-sounds-phrase-demo" className="btn-lab" onClick={phrase}>Play phrase demo ▬ ● ≋ ▬</button>
          <button type="button" data-testid="spore-sounds-apply" className="btn-lab" disabled={!nChosen} onClick={apply}>Apply {nChosen} chosen to the show</button>
          {msg && <span data-testid="spore-sounds-message" className="text-[var(--spore)]">{msg}</span>}
        </div>
        <p className="text-xs text-[var(--dim)]">The current clips stay live until you press Apply. Phrase demo uses your picks (else the current clips).</p>
        {a && d.types.map((t) => <TypeRow key={t.type} t={t} cands={a.candidates.filter((c) => c.type === t.type)} choice={choices[t.type]} onChoose={choose} playing={playing} setPlaying={setPlaying} />)}
      </div>
    </Panel>
  );
};
